import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { cookieConsentL as COPY } from "@/i18n/cookieConsent";
import { readConsent, setConsent, onConsentChange } from "@/lib/cookieConsent";

/**
 * Asks once, before any measurement: accept PostHog, or keep the necessary
 * storage only. Both buttons weigh the same, in look as in size. The choice
 * can be changed from the cookie page (CookieChoice below), which asks again.
 *
 * Compact on purpose (about 120px on a phone): the text in small type over
 * one row of two 44px buttons, one row on a wide screen. It sits on the
 * banner layer, under every dialog and menu (z-banner), and never covers a
 * page's main action: the page gets its height as bottom padding, and a
 * sticky booking bar rides above it (data-covers-bottom, ds/StickyActionBar).
 */
export default function CookieConsent() {
  const { lang } = useSettings();
  const t = pick(COPY, lang);
  const [choice, setChoice] = useState(readConsent);
  useEffect(() => onConsentChange(setChoice), []);
  const banner = useRef(null);
  // Fixed to the bottom, the banner covered the end of every page while it
  // waited for an answer: on a phone, the admin's "Turn on" two-factor button
  // and a form's last button sat under it. The page gets the banner's height
  // as bottom padding while it is shown, so everything can scroll above it.
  useEffect(() => {
    const node = banner.current;
    if (!node) return undefined;
    const body = document.body;
    const previous = body.style.paddingBottom;
    const reserve = () => { body.style.paddingBottom = `${node.offsetHeight}px`; };
    reserve();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(reserve) : null;
    observer?.observe(node);
    window.addEventListener("resize", reserve);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", reserve);
      if (previous) body.style.paddingBottom = previous;
      else body.style.removeProperty("padding-bottom");
    };
  }, [choice]);
  if (choice) return null;
  const button = "inline-flex min-h-11 flex-1 items-center justify-center rounded-md border border-touki-black bg-white px-3 py-1 text-center text-button-md font-semibold text-touki-black transition-colors duration-fast hover:bg-touki-light md:flex-none";
  // data-covers-bottom: an address field's suggestions and a sticky booking
  // bar keep clear of it (AddressAutocomplete's visibleBand, StickyActionBar).
  return (
    <section ref={banner} role="region" aria-label={t.label} data-testid="cookie-consent" data-covers-bottom=""
      className="fixed inset-x-0 bottom-0 z-banner border-t border-touki bg-white pt-2 shadow-tk-pop animate-in fade-in slide-in-from-bottom-4 duration-slow ease-tk-out tk-motion-slide md:pt-3"
      style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 sm:px-6 md:flex-row md:items-center md:gap-6 lg:px-8">
        <p className="flex-1 text-caption text-touki-black text-pretty md:text-body-sm">
          {t.text} <Link to="/cookies" className="font-semibold underline underline-offset-4">{t.more}</Link>
        </p>
        <div className="flex shrink-0 gap-2">
          <button type="button" onClick={() => setConsent("declined")} data-testid="cookie-decline" className={button}>{t.decline}</button>
          <button type="button" onClick={() => setConsent("accepted")} data-testid="cookie-accept" className={button}>{t.accept}</button>
        </div>
      </div>
    </section>
  );
}

/** On the cookie page: the current choice, and a way to be asked again. */
export function CookieChoice() {
  const { lang } = useSettings();
  const t = pick(COPY, lang);
  const [choice, setChoice] = useState(readConsent);
  useEffect(() => onConsentChange(setChoice), []);
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 text-sm" data-testid="cookie-choice">
      <span className="text-touki-muted">{t.current} <strong className="text-touki-black">{choice ? t[choice] : t.none}</strong></span>
      <button type="button" onClick={() => setConsent(null)} data-testid="cookie-change"
        className="min-h-11 px-4 rounded-md border border-touki-black font-semibold text-touki-black hover:bg-white">{t.change}</button>
    </div>
  );
}
